import { type FC, useCallback } from "react";
import { ChevronLeft, X } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/shared/components/ui/button";
import { useFlowSection } from "@/shared/hooks/useFlowSection";
import { EventType, trackEvent } from "@/shared/utils/core";
import type { FlowSection, FlowAnswers } from "@/shared/constants/flowTypes";
import { AUTH_ROUTES } from "@/shared/constants/paths";
import logoFull from "@/shared/assets/logo/logo-full.svg";

import { ScreenRenderer } from "./ScreenRenderer";

export interface FlowProps {
  section: FlowSection;
  onClose?: () => void;
  onBackFromStart?: () => void;
  initialStep?: number;
  initialAnswers?: FlowAnswers;
}

export const FlowController: FC<FlowProps> = ({
  section,
  onClose,
  onBackFromStart,
  initialStep = 0,
  initialAnswers = {},
}) => {
  const {
    progress,
    currentScreen,
    answers,
    errors,
    setAnswer,
    goNext,
    goBack,
    goToScreen,
    isFirstStep,
    isLastStep,
  } = useFlowSection({ section, initialStep, initialAnswers });

  const handleClose = useCallback(() => {
    trackEvent(EventType.ONBOARDING_SAVE_AND_CLOSE, {
      screen_id: currentScreen?.id,
      section_id: section.id,
    });
    onClose?.();
  }, [currentScreen?.id, section.id, onClose]);

  const showBackButton =
    currentScreen?.showBackButton !== false && (!isFirstStep || !!onBackFromStart);
  const showHeader = currentScreen?.hideHeader !== true;

  return (
    <div className="flex h-screen w-full flex-col">
      {showHeader && (
        <header className="shrink-0">
          <div className="flex items-center justify-between bg-white px-4 py-4 sm:px-6 sm:py-6">
            <div className="flex flex-1 min-h-9 items-center">
              {showBackButton && (
                <Button
                  variant="outline"
                  onClick={isFirstStep && onBackFromStart ? onBackFromStart : goBack}
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span className="hidden sm:inline">Back</span>
                </Button>
              )}
            </div>

            {currentScreen?.showHeaderLogo ? (
              <img src={logoFull} alt="Elayne" className="h-7" />
            ) : currentScreen?.headerComponent ? (
              <currentScreen.headerComponent answers={answers} />
            ) : (
              <p className="flex min-h-9 items-center text-sm sm:text-base text-center truncate px-2">
                {currentScreen?.headerTitle ?? section.title}
              </p>
            )}

            <div className="flex flex-1 min-h-9 items-center justify-end">
              {currentScreen?.showSignInLink ? (
                <div className="flex items-center gap-3">
                  <p className="hidden text-sm sm:block">Already an Elayne user?</p>
                  <Button variant="outline" size="md" asChild>
                    <Link to={AUTH_ROUTES.SIGN_IN} className="no-underline">
                      Sign in
                    </Link>
                  </Button>
                </div>
              ) : onClose ? (
                <Button variant="outline" onClick={handleClose} className="gap-2">
                  <X className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {section.allowSaveAndClose ? "Save & Close" : "Close"}
                  </span>
                </Button>
              ) : null}
            </div>
          </div>

          <div className="flex w-full">
            <div
              className="h-1 bg-brand transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
            <div className="h-1 flex-1 bg-stone-100" />
          </div>
        </header>
      )}

      <main className="flex-1 overflow-hidden bg-subtle">
        {currentScreen && (
          <ScreenRenderer
            screen={currentScreen}
            answers={answers}
            errors={errors}
            onAnswer={setAnswer}
            onContinue={goNext}
            onBack={showBackButton ? goBack : undefined}
            onGoToScreen={goToScreen}
            onCompleteFlow={section.onComplete}
            isLastStep={isLastStep}
          />
        )}
      </main>
    </div>
  );
};
